import { Box, Button, Stack, Typography, Card, CardContent } from "@mui/material";
import Grid from "@mui/material/Grid";
import { useState, useEffect } from "react";
import Translate from "../../utils/Translate";
import { getBrowserTypeForBankId } from "./bankIdBrowserType";
import { getBankIdGuiState } from "./bankIdGuiStateHelper";
import { getUserMessage } from "./bankIdUserMessageHelper";
import { useBankIdApi } from "./bankIdApi";

const returnUrlHashValue = "#returnFromApp";
const browserType = getBrowserTypeForBankId();
let timeout;

function createQrCodeUrl(qrCodeUrl, qrCodeQueryParams) {
  let url = `${qrCodeUrl}?t=${Date.now()}`;

  if (qrCodeQueryParams) {
    for (const [key, value] of Object.entries(qrCodeQueryParams)) {
      url += `&${key}=${encodeURIComponent(value)}`;
    }
  }

  return url;
}

const BankIdAuthentication = ({
  startAuthenticationUrl,
  postData,
  onStatusInitiated,
  onAuthenticationSuccess,
  onAuthenticationFailure = null,
  startQrCodeOnDesktop = true,
  fetchStatusIncludeCredentials = false,
  token = false,
  qrCodeQueryParams = {},
  sxCardOverride = {},
  sxTextOverride = {},
  classNameButtonTextOverride,
  classNameButtonContainedOverride,
}) => {
  sessionStorage.removeItem("courses");
  const [qrCodeButtonClicked, setQrCodeButtonClicked] = useState(false);
  const [bankIdQrUrl, setBankIdQrUrl] = useState(null);
  const [bankIdAutoStartUrl, setBankIdAutoStartUrl] = useState(null);
  const [bankIdCancelUrl, setBankIdCancelUrl] = useState(null);
  const [isIntializing, setIsIntitailizing] = useState(false);
  const [bankIdStatus, setBankIdStatus] = useState(null);
  const [bankIdStatusUrl, setBankIdStatusUrl] = useState(null);
  const [bankIdHintCode, setBankIdHintCode] = useState(null);
  const [tryingToOpenApp, setTryingToOpenApp] = useState(false);
  const [loginTypeAttempt, setLoginTypeAttempt] = useState("qr");
  const [eportActionIsSuccessful, setEportActionIsSuccessful] = useState(null);
  const [eportActionErrorMessageTranslationKey, setEportActionErrorMessageTranslationKey] = useState(null);
  const bankIdApi = useBankIdApi();
  const guiState = getBankIdGuiState(
    bankIdStatus,
    bankIdHintCode,
    isIntializing,
    browserType,
    tryingToOpenApp,
    bankIdQrUrl,
    qrCodeButtonClicked,
    startQrCodeOnDesktop,
    eportActionIsSuccessful
  );

  useEffect(() => {
    const start = async () => {
      const hash = window.location.hash;
      const existingSessionStatusUrl = localStorage.getItem("eportBankIdStatusUrl");

      if (hash.indexOf(returnUrlHashValue) > -1 && existingSessionStatusUrl) {
        // vi kommer tillbaka från mobilapp - hämta status
        await pollStatus(null, existingSessionStatusUrl);
      } else {
        // om desktop intitiera inloggning (på mobil visas knapp för att starta inloggning)
        if (browserType === "desktop" && startQrCodeOnDesktop) {
          const bankIdInitResponse = await initBankId();
          if(bankIdInitResponse) {
            startStatusPolling(bankIdInitResponse.qrCodeUrl, bankIdInitResponse.statusUrl);
          }
          // timeout = setTimeout(() => pollStatus(bankIdInitResponse.qrCodeUrl, bankIdInitResponse.statusUrl), 1000);
        }
      }
    };

    start();
  }, []); // initBankId och pollStatus borde vara med, men det skapar oändlig renderingsloop

  const pollStatus = async (qrCodeUrl, statusUrl) => {
    // ladda om qr-kod
    if (qrCodeUrl) {
      setBankIdQrUrl(createQrCodeUrl(qrCodeUrl, qrCodeQueryParams));
    }

    const bankIdStatusResponse = await bankIdApi.safeGetBankIdTransactionStatus(
      statusUrl,
      token,
      fetchStatusIncludeCredentials
    );

    //updatara status i GUI:t
    setBankIdStatus(bankIdStatusResponse.status);
    setBankIdHintCode(bankIdStatusResponse.hintCode);

    // lyckad bankId-autentisering
    if (bankIdStatusResponse.status === "complete") {
      setTryingToOpenApp(false);
      // .data innehåller eport-resultat som hämtats efter lyckad bankId autentisering
      setEportActionIsSuccessful(bankIdStatusResponse.data?.isSuccessful);
      setEportActionErrorMessageTranslationKey(bankIdStatusResponse.data?.errorMessageTranslationKey);

      // lyckad signering och lyckat eport-resultat
      if (bankIdStatusResponse.data?.isSuccessful) {
        if (onAuthenticationSuccess) {
          onAuthenticationSuccess(loginTypeAttempt, bankIdStatusResponse);
        }

        return;
      }

      // lyckad bankid-autentisering, men misslyckat eport-resultat - d.v.s. det som skulle göras/valideras i eport efter bankId-autentiseringen har misslyckats
      if (onAuthenticationFailure) {
        onAuthenticationFailure(loginTypeAttempt, bankIdStatusResponse);
      }

      return;
    }
    // misslyckad bankId-autentisering
    if (bankIdStatusResponse.status === "failed") {
      setTryingToOpenApp(false);
      localStorage.removeItem("eportBankIdStatusUrl");
      if (onAuthenticationFailure) {
        onAuthenticationFailure(loginTypeAttempt, bankIdStatusResponse);
      }

      return;
    }

    // om status inte är "complete" eller "failed" anropar vi igen efter en sekund
    timeout = setTimeout(() => pollStatus(qrCodeUrl, statusUrl), 1000);
  };

  const initBankId = async () => {
    setIsIntitailizing(true);
    setBankIdStatus(null);
    setBankIdAutoStartUrl(null);
    setBankIdQrUrl(null);
    setBankIdCancelUrl(null);
    setBankIdHintCode(null);
    localStorage.removeItem("eportBankIdStatusUrl");
    setEportActionIsSuccessful(null);
    setEportActionErrorMessageTranslationKey(null);

    // genom att använda exakt samma url kommer iphone att använda samma tab när man kommer tillbaka från bankid-appen
    // det är dock ok att lägga till ett hashvärde (#returnFromApp) för att markera att signering har skett. Däremot är det inte ok att lägga till querystring
    const currentUrl = window.location.href.split("#")[0]; // tabort tidigare hashvärde
    const request = {
      ...postData,
      language: Number(localStorage.lang ? localStorage.lang : 29), // 29 - swedish
      browserType: browserType,
      returnUrl: `${currentUrl}${returnUrlHashValue}`, // hashvärdet inidkerar att vi kommer tillbaka från bankidappen
    };

    const bankIdInitResponse = await bankIdApi.postBankIdInitialization(
      startAuthenticationUrl,
      request,
      token,
      fetchStatusIncludeCredentials
    );

    setIsIntitailizing(false);

    if (bankIdInitResponse.isSuccessful !== false) {
      setLoginTypeAttempt("qr");
      setBankIdAutoStartUrl(bankIdInitResponse.autoStartUrl);
      setBankIdCancelUrl(bankIdInitResponse.cancelUrl);
      setBankIdStatusUrl(bankIdInitResponse.statusUrl);
      // lägg till tidsparameter så att bilden måste laddas om varje gång
      setBankIdQrUrl(createQrCodeUrl(bankIdInitResponse.qrCodeUrl, qrCodeQueryParams));

      // spara i sessionStorage för att kunna användas om sidan laddas igen användare kommer tillbaka från appen
      localStorage.setItem("eportBankIdStatusUrl", bankIdInitResponse.statusUrl);

      const bankIdStatusResponse = await bankIdApi.safeGetBankIdTransactionStatus(
        bankIdInitResponse.statusUrl,
        token,
        fetchStatusIncludeCredentials
      );

      setBankIdStatus(bankIdStatusResponse.status);
      setBankIdHintCode(bankIdStatusResponse.hintCode);
      if (onStatusInitiated) {
        onStatusInitiated(bankIdInitResponse);
      }
      return bankIdInitResponse;
    } else {
      setBankIdStatus("failed");
      return null;
    }
  };

  const cancelBankIdLogin = async () => {
    if (timeout) {
      clearTimeout(timeout);
    }
    setTryingToOpenApp(false);
    setQrCodeButtonClicked(false);
    localStorage.removeItem("eportBankIdStatusUrl");
    setBankIdQrUrl(null);
    setBankIdAutoStartUrl(null);
    setBankIdStatus(null);
    setBankIdHintCode(null);

    await bankIdApi.postBankIdCancelTransaction(bankIdCancelUrl, token, fetchStatusIncludeCredentials);
    setBankIdCancelUrl(null);
    setBankIdStatusUrl(null);
    setBankIdStatus("failed");
    setBankIdHintCode("userCancel");
  };

  const startStatusPolling = (qrCodeUrl, statusUrl) => {
    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(async () => {
      await pollStatus(qrCodeUrl, statusUrl);
    }, 1000);
  };

  const openBankIdAppClick = async () => {
    const hash = window.location.hash;
    let qrUrl = bankIdQrUrl,
      statusUrl = bankIdStatusUrl,
      autoStartUrl = bankIdAutoStartUrl;

    if (hash.indexOf(returnUrlHashValue) > -1) {
      window.location.hash = "";
    }

    if (!bankIdAutoStartUrl) {
      const response = await initBankId();
      if (response) {
        qrUrl = response.qrCodeUrl;
        statusUrl = response.statusUrl;
        autoStartUrl = response.autoStartUrl;
      } else {
        return; // lyckades inte initialisera
      }
    }

    startStatusPolling(bankIdQrUrl, statusUrl);
    openBankIdApp(autoStartUrl);
  };

  const openBankIdApp = (url) => {
    setTryingToOpenApp(true);
    const loginType = browserType === "desktop" ? "desktop" : "app";
    setLoginTypeAttempt(loginType);

    window.location.replace(url);

    setTimeout(() => {
      setTryingToOpenApp(false);
      // display error after 10 seconds if app doesn't open
    }, 10000);
  };

  const loginUsingOtherDeviceClick = async () => {
    setQrCodeButtonClicked(true);
    if (!(bankIdQrUrl || bankIdStatusUrl)) {
      const response = await initBankId();
      if (response) {
        startStatusPolling(response.qrCodeUrl, response.statusUrl);
      }
    } else {
      startStatusPolling(bankIdQrUrl, bankIdStatusUrl);
    }
  };

  const tryAgainClick = async () => {
    const response = await initBankId();
    if (response) {
      startStatusPolling(response.qrCodeUrl, response.statusUrl);
    }
  };

  return (
    <Stack spacing={3} alignItems="center" style={{ marginBottom: "1rem" }}>
      {(guiState.showMessageAndQrCard || guiState.showFailedMessageForMobile) && (
        <Card sx={{ width: "300px", height: "350px", ...sxCardOverride }}>
          <CardContent>
            <Grid direction="column" justify="center" alignItems="center" alignContent="center" spacing={0}>
              {guiState.showSpinner && (
                <Box
                  component="img"
                  src="/static/Spinner_ePort.svg"
                  sx={{ margin: "auto", width: "75%", height: "75%", paddingBottom: "1rem" }}
                />
              )}
              <Typography>
                {getUserMessage(
                  tryingToOpenApp,
                  loginTypeAttempt,
                  bankIdStatus,
                  bankIdHintCode,
                  eportActionIsSuccessful,
                  eportActionErrorMessageTranslationKey
                )}
                <br />
              </Typography>

              {guiState.showStartAppButton && <img src={bankIdQrUrl} alt="Qr-Kod" />}
            </Grid>
          </CardContent>
        </Card>
      )}
      {/* {guiState.showFailedMessageForMobile
                ? getUserMessage(
                    tryingToOpenApp,
                    loginTypeAttempt,
                    bankIdStatus,
                    bankIdHintCode,
                    eportActionIsSuccessful,
                    eportActionErrorMessageTranslationKey
                )
                : null
            }  */}
      <Stack
        justifyContent="space-between"
        spacing={1}
        // height={guiState.showLoginUsingOtherDeviceButton ? "8rem" : "4rem"}
      >
        {!guiState.showLoginUsingOtherDeviceButton && guiState.showStartAppButton && (
          <Typography sx={{ textAlign: "center", textTransform: "lowercase", ...sxTextOverride }}>
            {Translate.get("Or")}
          </Typography>
        )}

        {guiState.showStartAppButton && (
          <Button
            className={classNameButtonContainedOverride ?? "stdBlueLoader"}
            fullWidth
            size="large"
            type="button"
            onClick={openBankIdAppClick}
            variant="contained"
            loading={tryingToOpenApp}
          >
            {(browserType === "desktop" && Translate.get("BankIdStartAppButtonDesktop")) ||
              Translate.get("BankIdStartAppButtonMobile")}
          </Button>
        )}
        {guiState.showStartAppButton && guiState.showLoginUsingOtherDeviceButton && (
          <Typography sx={{ textAlign: "center", textTransform: "lowercase", ...sxTextOverride }}>
            {Translate.get("Or")}
          </Typography>
        )}
        {guiState.showLoginUsingOtherDeviceButton && (
          <Button
            className={classNameButtonContainedOverride}
            variant="contained"
            size="large"
            onClick={loginUsingOtherDeviceClick}
            autoFocus={browserType === "desktop" ? true : false}
          >
            {Translate.get("BankIdLogInUsingOtherDevice")}
          </Button>
        )}
        {guiState.showCancelButton && (
          <Button className={classNameButtonTextOverride} fullWidth onClick={cancelBankIdLogin}>
            {Translate.get("Cancel")}
          </Button>
        )}

        {guiState.showTryAgainButton && (
          <Button className={classNameButtonTextOverride} fullWidth onClick={tryAgainClick}>
            {Translate.get("TryAgain")}
          </Button>
        )}
      </Stack>
      {/* debug-utskrift */}
      {/* <div>
                {JSON.errorMessageTranslationKey}

                {
                 getUserMessage(
                    tryingToOpenApp,
                    loginTypeAttempt,
                    bankIdStatus,
                    bankIdHintCode,
                    eportActionIsSuccessful,
                    eportActionErrorMessageTranslationKey
                )}

                {JSON.stringify(guiState, undefined, 2)}
            </div> */}
    </Stack>
  );
};

export default BankIdAuthentication;
